import { gql } from "@apollo/client";
import { faCalculator, faEuro } from "@fortawesome/free-solid-svg-icons";
import Accordion from "components/common/accordion/Accordion";
import { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import DateRangeInput from "components/common/form/inputs/date/DateRangeInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import FormActions from "components/common/form/partials/FormActions";
import If from "components/common/If";
import Page from "components/common/page/Page";
import Translate from "components/common/Translate";
import BookingFormChannel, {
    BookingFormChannelModelType
} from "components/entities/accommodation/booking/form/BookingFormChannel";
import BookingFormDetails, { DetailsType } from "components/entities/accommodation/booking/form/BookingFormDetails";
import BookingChannelDropdownInput, {
    BookingChannelDropdownDataItemType
} from "components/entities/accommodation/channel/BookingChannelDropdownInput";
import PersonDropdown from "components/entities/app/person/partials/inputs/PersonDropdown";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import { uuid } from "config/app-constants";
import { api, graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IdName } from "types/common-types";
import { DropdownInputPropsType } from "types/form-types";
import {
    BookingFormDataQuery,
    BookingFormDataQueryVariables,
    BookingDetailsInput
} from "types/graphql";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import bookingUtilities from "utilities/entities/accommodation/booking-utilities";
import { hasValidNumberValue } from "utilities/app-utilities";
import { copyDates } from "utilities/calendar-utilities";
import DateInput from "@/components/common/form/inputs/date/DateInput";


type FormRoomType = NonNullable<BookingFormDataQuery['rooms']>['items'][0];
type FormRoomTypeType = FormRoomType['roomType'];

type FormType = {
    id: number,
    season?: SeasonType,
    person?: PersonType,
    roomType?: FormRoomTypeType,
    preferredRoom?: FormRoomType,
    dateRage: Date[],
    details: DetailsType[]
    pricePerNight: number | string,
    guestCount: number | string,
    children?: string,
    total: number | string,
    channel?: BookingFormChannelModelType,
    note?: string,
    isCancelled: boolean,
    cancelledDate?: Date,
    isNoShowCancellation: boolean,
    paidAmount?: number | string
}

const query = gql`
query BookingFormData($id: Int, $roomTypeId: Int) {
    booking(id: $id) {
      person {
        name
        id
      }
      season {
        id
        name
        active
      }
      roomType {
        color
        defaultPrice
        name
        id
      }
      preferredRoomId
      isCancelled
      isNoShowCancellation
      cancelledDate
      nights
      guestCount
      children
      total
      incomeId
      paidAmount
      note
      channel {
        channel {
          commission
          type
          name
          id
        }
        total
        id
        externalReference
        isCancelled
      }
      id
      outDate
      preferredRoom {
        roomTypeId
        name
        id
      }
      inDate
      details {
        items {
          room {
            roomTypeId
            name
            id
          }
          price
          date
          id
        }
      }
      additionalCharge {
        items {
          amount
          date
          name
          id
        }
      }
    }
    rooms {
      items {
        name
        id
        roomType {
          color
          defaultPrice
          name
          id
        }
      }
    },
    season(where: {active: {eq: true}}) {
      name
      id
      active
    }
    personTag(where: {name: {eq: "Gost"}}) {
      name
      id
    }
    roomType(id: $roomTypeId) {
      id
      defaultPrice
      name
      color
    }
  }
`;

// const mutation = gql`
// mutation SaveBooking($model: BookingInput!) {
//     booking(model: $model) {
//       id
//     }
//   }
// `;

const BookingFormPage = () => {
    const context = useAppContext();
    const [searchParams] = useSearchParams();
    const params = useParams<{ id: string }>();
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [model, setModel] = useState<Partial<FormType>>({
        id: 0,
        guestCount: '',
        isCancelled: false,
        pricePerNight: '',
        total: '',
        dateRage: [],
        isNoShowCancellation: false,
        channel: {}
    });
    const [rooms, setRooms] = useState<FormRoomType[]>([]);
    const [roomTypes, setRoomTypes] = useState<FormRoomTypeType[]>([]);
    const [guestTag, setGuestTag] = useState<IdName>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');

    // #region field config
    const roomTypeDropdownConfig: DropdownInputPropsType<FormRoomTypeType> = {
        name: 'roomType',
        label: 'fields.roomType.name',
        required: true,
        getItemId: (item) => item.id,
        view: {
            inputView: i => i?.name,
        },
        dataSource: {
            data: (searchText) => {
                let theRoomTypes = [...roomTypes];
                if (searchText) {
                    theRoomTypes = theRoomTypes.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase()));
                }
                return theRoomTypes;
            }
        },
        onChange(s) {
            setModel({
                ...model,
                roomType: s,
                preferredRoom: undefined
            })
        },
    }

    const roomConfig: DropdownInputPropsType<FormRoomType> = {
        name: 'preferredRoom',
        dataSource: {
            data(searchText) {
                let theRooms = [...rooms];
                if (model.roomType) theRooms = theRooms.filter(x => x.roomType.id === model.roomType!.id)
                if (searchText) {
                    theRooms = theRooms.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase()));
                }
                return theRooms;
            },
        },
        getItemId: i => i?.id!,
        view: {
            inputView: i => i ? `${i.name} (${i.roomType.name})` : '',
        },
        onChange: s => setModel({
            ...model,
            preferredRoom: s,
            roomType: model.roomType || s?.roomType,
        }),
    }

    const onChangeDates = (datesVal: Date[] | undefined, modelData: Partial<FormType> | undefined = undefined): DetailsType[] => {
        let val = copyDates(datesVal || []);
        if (!val || !val.length) return [];
        const modelDetails = (modelData?.details || model.details || []);
        const allDates = modelDetails.flatMap(d => d.dates.map(date => ({ ...d, date })));
        const pricePerNight = modelData?.pricePerNight || model.pricePerNight || '';
        val[val.length - 1].setHours(-24);
        const dates = DateUtil.addMissingDates(val)!;
        const details: DetailsType[] = [];
        let lastDetail: DetailsType | undefined = undefined;
        dates.forEach(date => {
            const definedDetail = allDates.find(da => DateUtil.compareOnAsIsoDate(da.date, date)) // modelDetails?.find(d => d.dates.some(da => DateUtil.compareOnAsIsoDate(da, date)));
            if (definedDetail) {
                var firstDayIndex = definedDetail.dates.findIndex(d => DateUtil.compareOnAsIsoDate(d, datesVal![0]));
                if (firstDayIndex > 0) {
                    definedDetail.dates.splice(0, firstDayIndex);
                }

                if (lastDetail && lastDetail.price === definedDetail.price && lastDetail.room?.name === definedDetail.room?.name) {
                    details[details.length - 1].dates.push(date);
                } else {
                    details.push(definedDetail);
                    lastDetail = { ...definedDetail };
                }
            } else {
                if (lastDetail) {
                    details[details.length - 1].dates.push(date);
                } else {
                    details.push({
                        id: uuid(),
                        dates: [date],
                        price: pricePerNight,
                        room: model.preferredRoom
                    });
                    lastDetail = { ...details[details.length - 1] };
                }
            }
        });

        // correct the dates
        const finalDetails = details.map(d => {
            var lastDate = d.dates[d.dates.length - 1] > dates![dates!.length - 1] ? dates![dates!.length - 1] : d.dates[d.dates.length - 1];
            d.dates = DateUtil.addMissingDates([d.dates[0], lastDate])!;
            return d;
        });

        return finalDetails;
    }

    const calculateTotal = (): number | string | undefined => {
        if (model.isCancelled) return model.total || 0;
        return model.dateRage && model.dateRage.length && model.details?.length ?
            bookingUtilities.calculateTotalPrice(new Date(model.dateRage[0]), new Date(model.dateRage[model.dateRage!.length - 1]), model.details!.map(d => ({ date: new Date(d.dates[0]), price: Number(d.price) }))) :
            0;
    }

    // #endregion field config

    // #region load
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        const queryParams = {
            inDate: searchParams.get('inDate') && new Date(searchParams.get('inDate')!),
            outDate: searchParams.get('outDate') && new Date(searchParams.get('outDate')!),
            roomTypeId: searchParams.get('roomTypeId') && parseInt(searchParams.get('roomTypeId')!),
            pricePerNight: searchParams.get('pricePerNight') && parseInt(searchParams.get('pricePerNight')!),
        }
        try {
            const response = await graph<BookingFormDataQuery, BookingFormDataQueryVariables>({
                query,
                variables: {
                    id: params.id ? parseInt(params.id) : undefined,
                    roomTypeId: queryParams.roomTypeId ? Number(queryParams.roomTypeId) : 0,
                }
            });

            if (response.errors?.length) {
                throw response.errors[0];
            }

            const data = response.data?.booking;
            setRooms(response.data!.rooms!.items);
            const roomTypesFiltered: FormRoomTypeType[] = [];
            response.data!.rooms!.items.forEach(r => {
                if (!roomTypesFiltered.find(x => x.id === r.roomType.id)) {
                    roomTypesFiltered.push(r.roomType);
                }
            })
            setRoomTypes(roomTypesFiltered);
            setGuestTag(response.data?.personTag || undefined);
            if (data) {
                const currentDetails = data.details!.items.map(d => ({
                    id: uuid(),
                    price: d.price,
                    room: response.data!.rooms!.items.find(r => r.id === d.room.id),
                    dates: [new Date(d.date)],
                })) as any as DetailsType[];

                const total = data.isCancelled ? data.total : bookingUtilities.calculateTotalPrice(new Date(data.inDate), new Date(data.outDate), data.details?.items);
                const channelTotal = data.channel ? bookingUtilities.calculateChannelCommission(total, { type: data.channel!.channel.type, commission: data.channel!.channel.commission }) : undefined;
                const theModel = {
                    channel: {
                        channel: data.channel?.channel as BookingChannelDropdownDataItemType,
                        id: data.channel?.id || 0,
                        total: data.channel?.total === channelTotal ? '' : (data.channel?.total || ''),
                        externalReference: data.channel?.externalReference || '',
                        isCancelled: data.channel?.isCancelled || false
                    },
                    children: data.children || '',
                    id: data.id,
                    guestCount: data.guestCount,
                    isCancelled: data.isCancelled,
                    cancelledDate: data.cancelledDate ? new Date(data.cancelledDate) : undefined,
                    isNoShowCancellation: data.isNoShowCancellation,
                    note: data.note || '',
                    person: data.person as PersonType,
                    pricePerNight: data.details?.items[0]?.price,
                    preferredRoom: response.data!.rooms!.items.find(r => r.id === data.preferredRoomId),
                    roomType: data.roomType,
                    season: data.season as SeasonType,
                    total: !data.isCancelled && data.total === total ? '' : data.total,
                    dateRage: DateUtil.addMissingDates([new Date(data.inDate), new Date(data.outDate)]),
                    paidAmount: data.paidAmount || '',
                    details: currentDetails
                };

                theModel.details = onChangeDates([new Date(data.inDate), new Date(data.outDate)], theModel);
                setModel(theModel)
            } else {
                let theModel = {
                    ...model,
                    dateRage: queryParams.inDate && queryParams.outDate ? DateUtil.addMissingDates([queryParams.inDate, queryParams.outDate]) : [],
                    details: [],
                    pricePerNight: queryParams.pricePerNight ? queryParams.pricePerNight.toString() : '',
                    season: response.data!.season!,
                    roomType: response.data!.roomType || undefined,
                    id: 0,
                } as Partial<FormType>;

                if (queryParams.inDate && queryParams.outDate) {
                    theModel.details = onChangeDates([queryParams.inDate, queryParams.outDate], theModel)
                }

                setModel(theModel)
            }
            setComponentState('');
        } catch (error: any) {
            context.notify('error', error.message, Array.isArray(error) ? error[0] : error);
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load

    function onIsCancelledChange(value: boolean | undefined) {
        const theModel = model;
        theModel.isCancelled = value || false;
        theModel.cancelledDate = value ? new Date() : undefined;
        if (!value) {
            theModel.isNoShowCancellation = false;
        }

        setModel({ ...model })
    }

    const onSubmit = async () => {
        setFormSubmitted(true);

        const inDate = model.dateRage![0];
        const outDate = model.dateRage![model.dateRage!.length - 1];
        let details: BookingDetailsInput[] = [];
        let total = 0;

        if (!model.isCancelled) {
            let previousDetail: BookingDetailsInput | undefined = undefined;
            for (let i = 0; i < (model.details?.length || 0); i++) {
                const detail = model.details![i];
                for (let j = 0; j < detail.dates.length; j++) {
                    const bookingDetailInput: BookingDetailsInput = {
                        date: DateUtil.toIsoDate(new Date(detail.dates[j])),
                        id: 0,
                        price: detail.price || previousDetail?.price || 0,
                        roomId: detail.room?.id || previousDetail?.roomId || 0
                    };

                    bookingDetailInput.price = Number(bookingDetailInput.price);
                    details.push(bookingDetailInput);
                    previousDetail = bookingDetailInput;
                }
            }

            total = hasValidNumberValue(model.total) ? Number(model.total) :
                bookingUtilities.calculateTotalPrice(new Date(inDate), outDate, details.map(d => ({ date: new Date(d.date), price: d.price })));
        }

        let data = {
            personId: model.person!.id,
            seasonId: model.season!.id,
            roomTypeId: model.roomType!.id,
            preferredRoomId: model.preferredRoom?.id,
            inDate: DateUtil.toIsoDate(inDate),
            outDate: DateUtil.toIsoDate(outDate),
            guestCount: Number(model.guestCount),
            children: model.children,
            total: total,
            note: model.note,
            bookingDate: new Date().toISOString(),
            cancelledDate: model.cancelledDate ? model.cancelledDate.toISOString() : undefined,
            isNoShowCancellation: model.isNoShowCancellation || false,
            id: model.id!,
            paidAmount: Number(model.paidAmount) || undefined,
            details,
            channel: model.channel?.channel?.id ? {
                channelId: model.channel.channel.id,
                id: model.channel.id || 0,
                externalReference: model.channel.externalReference,
                total: model.isCancelled ? 0 : (Number(hasValidNumberValue(model.channel.total) ? Number(model.channel.total) : bookingUtilities.calculateChannelCommission(Number(total), model.channel!.channel!))),
                isCancelled: model.channel.isCancelled || false
            } : undefined
        };

        try {
            // const response = await graph<SaveBookingMutation, SaveBookingMutationVariables>({
            //     query: mutation,
            //     variables: {
            //         model: data
            //     }
            // });

            const response = await api.post<{ id: number }>("/booking/save", data);

            if (!response.success) {
                throw new Error();
            }

            context.notify('success', 'request.save.success');
            context.navigate(AppRoutes.ACCOMMODATION_BOOKING_VIEW, { id: response.data!.id }, {
                replace: true
            });
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0] : error);
            throw error;
        } finally {
            setFormSubmitted(false);
        }
    };

    return (
        <Page title="pages.accommodation.booking.form" state={componentState}>
            <Form name="booking-form-page" onSubmit={onSubmit}>
                <fieldset>
                    <SeasonDropdown
                        onChange={(value) => setModel({ ...model, season: value })}
                        value={model.season}
                        required
                    />
                    <PersonDropdown
                        onChange={value => setModel({ ...model, person: value })}
                        value={model.person}
                        required
                        name="person"
                        addPerson={{
                            model: guestTag ? {
                                tags: [{
                                    id: uuid(),
                                    name: guestTag.name,
                                    tagId: guestTag.id
                                }]
                            } : undefined
                        }}
                    />
                    <DateRangeInput
                        value={model.dateRage}
                        name="dateRage"
                        format={DateFormats.SHORT}
                        label="fields.date.name"
                        view={model.dateRage?.length ? 'date' : 'month'}
                        onChange={(value) => setModel({
                            ...model,
                            dateRage: value,
                            details: onChangeDates(value),
                        })}
                        required
                        description={<span><Translate
                            i18nKey='accommodation.booking.totalNights' />: {(model.dateRage?.length || 1) - 1}</span>}
                        calculateAsNights
                        selectOnly="start-end"
                    />
                    <DropdownInput
                        {...roomTypeDropdownConfig}
                        value={model.roomType}
                    />
                    <Input
                        name="guestCount"
                        label="fields.guest-count.name"
                        required
                        min={1}
                        type="number"
                        value={model.guestCount?.toString() || ''}
                        onChange={(value) => setModel({ ...model, guestCount: value })}
                    />
                    <Input
                        name="children"
                        label="fields.children.name"
                        type="text"
                        value={model.children?.toString() || ''}
                        onChange={(value) => setModel({ ...model, children: value })}
                    />
                    <DropdownInput
                        {...roomConfig}
                        label="fields.preferredRoom.name"
                        value={model.preferredRoom}
                    />
                </fieldset>
                <fieldset>
                    <BookingChannelDropdownInput
                        onChange={(value) => setModel({
                            ...model,
                            channel: {
                                ...model.channel,
                                channel: value
                            }
                        })}
                        value={model.channel?.channel}
                    />
                    <If condition={model.channel?.channel?.id}>
                        <Input
                            label="fields.external-reference.name"
                            name="externalReference"
                            required
                            value={model.channel?.externalReference || ''}
                            onChange={(value) => setModel({
                                ...model,
                                channel: {
                                    ...model.channel,
                                    externalReference: value
                                }
                            })}
                        />
                        <CheckboxInput
                            name="isCancelledBookingChannel"
                            label="fields.channelIsCancelled.name"
                            value={model.channel?.isCancelled || false}
                            onChange={(value) => setModel({
                                ...model,
                                channel: {
                                    ...model.channel,
                                    isCancelled: value
                                }
                            })}
                        />
                    </If>
                </fieldset>
                <If condition={!model.isCancelled}>
                    <fieldset>
                        <hr />
                        <BookingFormDetails
                            details={model.details || []}
                            channel={model.channel}
                            rooms={rooms}
                            preferredRoom={model.preferredRoom}
                            roomType={model.roomType}
                            setDetails={(value) => setModel({ ...model, details: value })}
                        />
                        <hr />
                    </fieldset>
                </If>
                <fieldset>
                    <Accordion name="totals" title="accommodation.booking.form.totalDataFields">
                        <Input
                            name="total"
                            label="fields.total.name"
                            min={0}
                            required={model.isCancelled}
                            type="number"
                            placeholder={context.t('form.placeholders.leaveEmptyForDefault')}
                            value={model.total?.toString() || ''}
                            onChange={(value) => setModel({ ...model, total: value })}
                            suffix={[
                                {
                                    icon: faCalculator,
                                    name: 'calculator',
                                    title: 'common.calculate',
                                    hidden: () => model.isCancelled || false,
                                    onClick: () => setModel({
                                        ...model,
                                        total: calculateTotal()
                                    })
                                },
                                { icon: faEuro, name: 'euro', title: 'euro' }
                            ]}
                        />
                        <If condition={!model.isCancelled}>
                            <Input
                                name="paidAmount"
                                label="fields.paidAmount.name"
                                min={0}
                                placeholder={context.t('form.placeholders.leaveEmptyForDefault')}
                                type="number"
                                value={model.paidAmount?.toString() || ''}
                                onChange={(value) => setModel({ ...model, paidAmount: value })}
                                suffix={[
                                    {
                                        icon: faCalculator,
                                        name: 'calculator',
                                        title: 'common.calculate',
                                        onClick: () => setModel({
                                            ...model,
                                            paidAmount: model.total
                                        })
                                    },
                                    { icon: faEuro, name: 'euro', title: 'euro' }
                                ]}
                            />
                        </If>
                        <If condition={!model.isCancelled && model.channel?.channel?.id}>
                            <BookingFormChannel
                                value={model.channel}
                                onChange={(value) => {
                                    setModel({ ...model, channel: value })
                                }}
                                total={model.total}
                            />

                        </If>
                    </Accordion>
                    <CheckboxInput
                        name="isCancelled"
                        label="fields.isCancelled.name"
                        value={model.isCancelled}
                        onChange={onIsCancelledChange}
                    />
                    <If condition={model.isCancelled}>
                        <DateInput
                            label="fields.cancelledDate.name"
                            name="cancelledDate"
                            required
                            value={model.cancelledDate}
                            onChange={(v) => setModel({ ...model, cancelledDate: v })}
                        />
                        <CheckboxInput
                            label="fields.isNoShowCancellation.name"
                            name="isNoShowCancellation"
                            value={model.isNoShowCancellation}
                            onChange={(v) => setModel({ ...model, isNoShowCancellation: v || false })}
                        />
                    </If>
                </fieldset>
                <fieldset>
                    <Textarea
                        name="notes"
                        label="fields.note.name"
                        value={model.note?.toString() || ''}
                        onChange={(value) => setModel({ ...model, note: value })}
                    />
                </fieldset>
                <FormActions disableInputs={formSubmitted} />
            </Form>
        </Page>
    );
};

export default BookingFormPage;
